import React from 'react';
import { palette } from '@citrite/web-ui-component';
import styled from '@emotion/styled';
import { CustomLinkBanner } from 'App/CustomLinkBanner/CustomLinkBanner';
import { GenericErrorBoundary } from 'App/GenericErrorBoundary';
import { Actions } from 'App/Header/Actions';
import { SmallFormTitle } from 'App/Header/SmallFormTitle';
import { Title } from 'App/Header/Title';
import { useLogoCompressionBreakpoint } from 'App/Header/useLogoCompressionBreakpoint';
import { ItemSearchBar } from 'App/Masthead/ItemSearchBar';
import { OfflineBanner } from 'Components/AppLayout';
import { ConditionalRenderer } from 'Components/ConditionalRenderer';
import { useIsDetailedDesktopTileEnabled } from 'Configuration/hooks';
import { useConfigurationContext } from 'Configuration/useConfigurationContext';
import { environment } from 'Environment';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { useFeatureCanary } from 'utils/useFeatureCanary';
import { centerContentWidthPX } from 'Workspace/dimensions';
import { Banner } from './Banner';
import { useFormFactor } from './useFormFactor';

const StyledContent = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
`;

const StyledAppHeaderContainer = styled.div`
	margin: 0 auto;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	width: 100%;
	border: solid ${palette.grey300};
	border-width: 0 0 1px 0;
	overflow: hidden;

	& > *:last-child {
		border-width: 0;
	}
`;

const StyledCenterContentWrapper = styled.div`
	max-width: ${centerContentWidthPX}px;
	width: 100%;
	margin: 0 auto;
`;

interface HeaderContentProps {
	isLargeFormFactor: boolean;
	isDetailedDesktopTileEnabled: boolean;
}

const CustomLinkSection = () => {
	const isCustomLinkEnabled = useFeatureCanary(FeatureFlag.EnableCustomLinks);
	const customLinkData =
		useConfigurationContext().workspaceConfiguration?.pinnedLinks?.pinnedLink || [];
	const { isSmallFormFactor } = useFormFactor();

	if (!isCustomLinkEnabled || !customLinkData.length) {
		return null;
	}

	return (
		<CustomLinkBanner
			customLinkData={customLinkData}
			isSmallFormFactor={isSmallFormFactor}
		/>
	);
};

const HeaderContent = ({
	isLargeFormFactor,
	isDetailedDesktopTileEnabled,
}: HeaderContentProps) => {
	const { compressedDims } = useLogoCompressionBreakpoint();

	return (
		<>
			{!environment.citrixCloudConnected && <OfflineBanner />}
			<Banner>
				<ConditionalRenderer
					flag={isDetailedDesktopTileEnabled && isLargeFormFactor}
					OnComponent={StyledCenterContentWrapper}
				>
					<StyledContent data-testid="header-content">
						{isLargeFormFactor ? (
							<Title compressedDims={compressedDims} />
						) : (
							<SmallFormTitle />
						)}
						{isLargeFormFactor && <ItemSearchBar />}
						<Actions />
					</StyledContent>
				</ConditionalRenderer>
				{!isLargeFormFactor && <ItemSearchBar />}
			</Banner>
			{isLargeFormFactor && <CustomLinkSection />}
		</>
	);
};

export const _DaasHeader = () => {
	const isDetailedDesktopTileEnabled = useIsDetailedDesktopTileEnabled();
	const { isLargeFormFactor } = useFormFactor();

	const StyledAppHeaderContainerProps = { 'data-testid': 'app-header-container' };

	return (
		<ConditionalRenderer
			flag={isDetailedDesktopTileEnabled && isLargeFormFactor}
			OnComponent={StyledAppHeaderContainer}
			OnComponentProps={StyledAppHeaderContainerProps}
		>
			<HeaderContent
				isLargeFormFactor={isLargeFormFactor}
				isDetailedDesktopTileEnabled={isDetailedDesktopTileEnabled}
			/>
		</ConditionalRenderer>
	);
};

export const DaasHeader = () => {
	return (
		<GenericErrorBoundary renderProp={() => <_DaasHeader />} name="DaasHeaderError" />
	);
};
